import { ZodError } from "zod";
import { ProductError, type ProductErrorCode } from "../../../contracts/errors.ts";

const SECRET_PATTERNS = [
  /AIza[0-9A-Za-z_-]{10,}/g,
  /Bearer\s+\S+/gi,
  /(?:api[_-]?key|x-goog-api-key)\s*[:=]\s*["']?[^"'\s]+/gi,
];

export function sanitizeAiErrorText(message: string): string {
  let sanitized = message;
  for (const pattern of SECRET_PATTERNS) {
    sanitized = sanitized.replace(pattern, "[redacted]");
  }
  return sanitized;
}

export function translateAiError(error: unknown): ProductError {
  if (error instanceof ProductError) {
    return error;
  }
  if (
    error instanceof ZodError ||
    (typeof error === "object" &&
      error !== null &&
      "issues" in error &&
      "name" in error &&
      String(error.name).toLowerCase().includes("zod"))
  ) {
    return new ProductError("invalid_response", "The model returned an invalid vocabulary payload.");
  }
  const raw = error instanceof Error ? error.message : String(error);
  const message = sanitizeAiErrorText(raw);
  const lower = message.toLowerCase();
  const map: Array<[string[], ProductErrorCode, string, boolean]> = [
    [["abort", "cancel"], "cancelled", "Generation was cancelled.", false],
    [["offline", "failed to fetch", "network"], "offline", "The network request failed. Check your connection.", true],
    [["api key", "unauthenticated", "401", "invalid x-goog-api-key"], "invalid_key", "This Gemini API key was rejected.", false],
    [["429", "resource exhausted", "rate"], "rate_limited", "Gemini is rate-limiting requests. Wait and retry.", true],
    [["404", "not found", "not supported"], "model_unavailable", "The configured Gemini model is unavailable.", false],
    [["timeout", "deadline"], "timeout", "The Gemini request timed out.", true],
    [["failed to parse", "invalid payload", "transforms cannot"], "invalid_response", "The model returned an invalid vocabulary payload.", true],
  ];
  for (const [needles, code, text, retryable] of map) {
    if (needles.some((needle) => lower.includes(needle))) {
      return new ProductError(code, text, retryable);
    }
  }
  return new ProductError("generation_failed", "Generation failed. Try again.", true);
}
